/**
 * Global paywall modal — triggered by any 402/BillingError.
 *
 * Self-serve credit packs are the primary CTA (CreditPackPicker → Stripe). A
 * secondary link opens the Enterprise & bulk inquiry form for teams/volume.
 * If no packs are configured on the env, CreditPackPicker falls back to the
 * inquiry form automatically. BYOK_REQUIRED opens the Claude key settings
 * instead, and BILLING_HOLD points to support.
 */

import { useState } from 'react';
import { Sparkles, ArrowLeft } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../ui/dialog';
import { useUpgradeModal } from '../../stores/upgradeModalStore';
import { UpgradeRequestForm } from './UpgradeRequestForm';
import { CreditPackPicker } from './CreditPackPicker';
import { ClaudeTokenModal } from '../agent-chat/ClaudeTokenModal';
import { Button } from '../ui/button';

export function UpgradeModal() {
  const { isOpen, code, message, close } = useUpgradeModal();
  const [showEnterprise, setShowEnterprise] = useState(false);
  const [keyOpen, setKeyOpen] = useState(false);

  const headline = showEnterprise ? 'Enterprise & bulk' : resolveHeadline(code);
  const subtext = showEnterprise
    ? "Tell us about your team and volume — we'll get back to you with pricing."
    : resolveSubtext(code, message);

  function handleOpenChange(open: boolean) {
    if (!open) {
      close();
      setShowEnterprise(false);
    }
  }

  function openKeySettings() {
    handleOpenChange(false);
    setKeyOpen(true);
  }

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-2 bg-primary">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <DialogTitle>{headline}</DialogTitle>
            <DialogDescription>{subtext}</DialogDescription>
          </DialogHeader>

          {code === 'BYOK_REQUIRED' ? (
            <Button className="w-full" onClick={openKeySettings}>
              Add your model key
            </Button>
          ) : code === 'BILLING_HOLD' ? (
            <Button asChild className="w-full">
              <a href="mailto:support@4klabs.ai">Contact us</a>
            </Button>
          ) : showEnterprise ? (
            <div className="space-y-3">
              <button
                type="button"
                onClick={() => setShowEnterprise(false)}
                className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
              >
                <ArrowLeft className="h-3.5 w-3.5" /> Back to credits
              </button>
              <UpgradeRequestForm source="upgrade_modal" onSuccess={() => setTimeout(() => handleOpenChange(false), 1800)} />
            </div>
          ) : (
            <>
              {/* Self-serve credit packs when Stripe is configured; otherwise the
                  inquiry form (CreditPackPicker falls back automatically). */}
              <CreditPackPicker
                fallback={<UpgradeRequestForm source="upgrade_modal" onSuccess={() => setTimeout(() => handleOpenChange(false), 1500)} />}
              />
              <button
                type="button"
                onClick={() => setShowEnterprise(true)}
                className="mt-3 w-full text-center text-xs text-muted-foreground hover:text-foreground"
              >
                Buying for a team or in bulk? Contact us →
              </button>
            </>
          )}
        </DialogContent>
      </Dialog>
      <ClaudeTokenModal open={keyOpen} onClose={() => setKeyOpen(false)} />
    </>
  );
}

function resolveHeadline(code: string | null): string {
  if (code === 'OUT_OF_CREDITS') return "You're out of credits";
  if (code === 'BYOK_REQUIRED') return 'Add your model key to continue';
  if (code === 'BILLING_HOLD') return 'Your account is on hold';
  if (code === 'EXPORT_NOT_ALLOWED') return 'Watermark-free export needs credits';
  if (code === 'LENGTH_EXCEEDED') return 'Longer videos need credits';
  if (code === 'FOUR_K_NOT_ALLOWED') return '4K export needs credits';
  if (code === 'NO_PLAN') return 'Buy credits to continue';
  return 'Buy credits to continue';
}

function resolveSubtext(code: string | null, message: string | null): string {
  if (message && !/claude|anthropic/i.test(message)) return message;
  if (code === 'BYOK_REQUIRED') return 'This run uses your own model key, and none is saved yet.';
  if (code === 'BILLING_HOLD') return "Your account is on hold. Contact us and we'll sort it out.";
  return 'Top up your credits to keep creating. Credits never expire.';
}
